import type { Industry } from '@/api/types';

export type Status = 'active' | 'inactive' | 'pending';

export const STATUSES: { value: Status; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'pending', label: 'Pending review' },
];

export function statusOf(industry: Industry): Status {
  if (industry.status === 'pending') return 'pending';
  return industry.is_active ? 'active' : 'inactive';
}

export type SortKey = 'name' | 'description' | 'idea_count' | 'status';
export type Sort = { key: SortKey; order: 'asc' | 'desc' };

const SORT_VALUE: Record<SortKey, (i: Industry) => string | number> = {
  name: (i) => i.name.toLowerCase(),
  description: (i) => (i.description ?? '').toLowerCase(),
  idea_count: (i) => i.idea_count,
  status: (i) => STATUSES.findIndex((s) => s.value === statusOf(i)),
};

function compare(a: string | number, b: string | number): number {
  return typeof a === 'number' && typeof b === 'number' ? a - b : String(a).localeCompare(String(b));
}

function matchesSearch(industry: Industry, needle: string): boolean {
  if (!needle) return true;
  return industry.name.toLowerCase().includes(needle) || (industry.description ?? '').toLowerCase().includes(needle);
}

/** The rows matching the search and status filter, in the chosen order (ties by name). */
export function visibleIndustries(rows: Industry[], search: string, statuses: string[], sort: Sort): Industry[] {
  const needle = search.trim().toLowerCase();
  const value = SORT_VALUE[sort.key];
  const direction = sort.order === 'asc' ? 1 : -1;
  return rows
    .filter((i) => matchesSearch(i, needle) && (statuses.length === 0 || statuses.includes(statusOf(i))))
    .sort((a, b) => direction * compare(value(a), value(b)) || a.name.localeCompare(b.name));
}

/** Clicking the current column flips its order; a new column starts ascending, Ideas descending. */
export function nextSort(current: Sort, key: SortKey): Sort {
  if (current.key === key) return { key, order: current.order === 'asc' ? 'desc' : 'asc' };
  return { key, order: key === 'idea_count' ? 'desc' : 'asc' };
}

/** Title, subtitle and submit label of the add / edit dialog. */
export function industryDialogText(industry?: Industry): { title: string; subtitle: string; submitLabel: string } {
  if (!industry) {
    return { title: 'Add industry', subtitle: 'It is approved and offered on the submit form straight away.', submitLabel: 'Add industry' };
  }
  if (industry.status === 'pending') {
    const origin = industry.suggested_by ? `Suggested by ${industry.suggested_by.display_name}` : 'Proposed by research';
    return {
      title: 'Edit proposed industry',
      subtitle: `${origin} · ${industry.idea_count} idea(s) use it. Fix the name before approving if needed.`,
      submitLabel: 'Save changes',
    };
  }
  return { title: 'Edit industry', subtitle: `${industry.idea_count} idea(s) use it`, submitLabel: 'Save changes' };
}
